/* FLRC — review lists (first-party reviews, 12 Oct 2026). Progressive:
   without this file every server-rendered review shows, newest first, and
   each photo is a plain link to the full image.

   For each [data-flrc-rvl] root it adds, over the cards already in the page
   (.flrc-rvl__card, see snippets/flrc-review-card.liquid):
     - search, sort, rating filter and a "With media" toggle;
     - a product filter when the root has data-flrc-rvl-products and the
       cards cover more than one product (the All Reviews page);
     - "Show more", 10 cards at a time;
     - a <dialog> photo viewer for [data-flrc-rvl-photo] links.
   Pure DOM work: no fetch, no Klaviyo. Cards must be the only children of
   their list element, which this reorders in place. */
(() => {
  const STEP = 10;
  const SORTS = [
    ['recent', 'Most recent'],
    ['high', 'Highest rated'],
    ['low', 'Lowest rated'],
    ['photos', 'With photos first'],
  ];
  let uid = 0;

  const make = (tag, attrs = {}, text) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
    if (text != null) node.textContent = text;
    return node;
  };

  const select = (id, label, options) => {
    const wrap = make('div', { class: 'flrc-rvl__field' });
    const lab = make('label', { class: 'visually-hidden', for: id }, label);
    const sel = make('select', { class: 'flrc-rvl__control', id });
    options.forEach(([value, text]) => sel.append(make('option', { value }, text)));
    wrap.append(lab, sel);
    return { wrap, sel };
  };

  const initList = (root) => {
    const cards = [...root.querySelectorAll('.flrc-rvl__card')];
    if (!cards.length) return;
    const list = cards[0].parentElement;
    const n = ++uid;
    const data = cards.map((card, i) => ({
      card,
      i,
      rating: parseInt(card.dataset.rating, 10) || 0,
      date: card.dataset.date || '',
      photos: card.dataset.hasPhotos === 'true',
      product: card.dataset.product || '',
      search: card.dataset.search || card.textContent.toLowerCase(),
    }));
    const state = { q: [], sort: 'recent', rating: 0, product: '', media: false, shown: STEP };

    // Controls.
    const bar = make('div', { class: 'flrc-rvl__controls', role: 'group', 'aria-label': 'Search and filter reviews' });

    const searchWrap = make('div', { class: 'flrc-rvl__search' });
    searchWrap.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 11l3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>';
    const searchId = `flrc-rvl-q-${n}`;
    const search = make('input', {
      class: 'flrc-rvl__control',
      id: searchId,
      type: 'search',
      placeholder: 'Search reviews',
      autocomplete: 'off',
    });
    searchWrap.prepend(make('label', { class: 'visually-hidden', for: searchId }, 'Search reviews'));
    searchWrap.append(search);
    bar.append(searchWrap);

    const sort = select(`flrc-rvl-sort-${n}`, 'Sort reviews', SORTS);
    bar.append(sort.wrap);

    const rating = select(`flrc-rvl-rating-${n}`, 'Filter by rating', [
      ['0', 'All ratings'],
      ...[5, 4, 3, 2, 1].map((s) => [String(s), s === 1 ? '1 star' : `${s} stars`]),
    ]);
    bar.append(rating.wrap);

    let product = null;
    if (root.hasAttribute('data-flrc-rvl-products')) {
      const seen = new Map();
      cards.forEach((card) => {
        const key = card.dataset.product;
        if (key && !seen.has(key)) seen.set(key, card.dataset.productTitle || key);
      });
      if (seen.size > 1) {
        // Products first, by title; store reviews last.
        const opts = [...seen].sort((a, b) => (a[0] === 'store') - (b[0] === 'store') || a[1].localeCompare(b[1]));
        product = select(`flrc-rvl-product-${n}`, 'Filter by product', [
          ['', 'All products'],
          ...opts.map(([key, title]) => [key, key === 'store' ? 'Store reviews' : title]),
        ]);
        bar.append(product.wrap);
      }
    }

    let media = null;
    if (data.some((d) => d.photos)) {
      media = make('button', { class: 'flrc-rvl__toggle', type: 'button', 'aria-pressed': 'false' }, 'With media');
      bar.append(media);
    }

    const status = make('p', { class: 'flrc-rvl__status', role: 'status', 'aria-live': 'polite' });
    const none = make('p', { class: 'flrc-rvl__none', hidden: '' }, 'No reviews match. ');
    const clear = make('button', { type: 'button' }, 'Clear filters');
    none.append(clear);
    const foot = make('div', { class: 'flrc-rvl__foot' });
    const more = make('button', { class: 'flrc-rvl__more', type: 'button' }, 'Show more');
    foot.append(more);

    list.before(bar, status);
    list.after(none, foot);

    const compare = {
      recent: (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.i - b.i),
      high: (a, b) => b.rating - a.rating || compare.recent(a, b),
      low: (a, b) => a.rating - b.rating || compare.recent(a, b),
      photos: (a, b) => b.photos - a.photos || compare.recent(a, b),
    };

    const render = (revealFrom) => {
      const match = data.filter(
        (d) =>
          (!state.rating || d.rating === state.rating) &&
          (!state.product || d.product === state.product) &&
          (!state.media || d.photos) &&
          state.q.every((word) => d.search.includes(word))
      );
      match.sort(compare[state.sort]);
      const rest = data.filter((d) => !match.includes(d));
      list.append(...match.map((d) => d.card), ...rest.map((d) => d.card));
      match.forEach((d, i) => (d.card.hidden = i >= state.shown));
      rest.forEach((d) => (d.card.hidden = true));

      const visible = Math.min(state.shown, match.length);
      const filtered = match.length !== data.length;
      status.textContent = filtered
        ? `${match.length} of ${data.length} reviews match. Showing ${visible}.`
        : `Showing ${visible} of ${data.length} reviews`;
      none.hidden = match.length > 0;
      foot.hidden = match.length <= state.shown;

      // After "Show more", move focus to the first new card so keyboard
      // and screen reader users carry on reading where the list grew.
      if (revealFrom != null && match[revealFrom]) {
        const card = match[revealFrom].card;
        card.setAttribute('tabindex', '-1');
        card.focus();
      }
    };

    const refilter = () => {
      state.shown = STEP;
      render();
    };

    let timer = 0;
    search.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        state.q = search.value.toLowerCase().split(/\s+/).filter(Boolean);
        refilter();
      }, 150);
    });
    sort.sel.addEventListener('change', () => {
      state.sort = sort.sel.value;
      refilter();
    });
    rating.sel.addEventListener('change', () => {
      state.rating = parseInt(rating.sel.value, 10) || 0;
      refilter();
    });
    if (product) {
      product.sel.addEventListener('change', () => {
        state.product = product.sel.value;
        refilter();
      });
    }
    if (media) {
      media.addEventListener('click', () => {
        state.media = !state.media;
        media.setAttribute('aria-pressed', String(state.media));
        refilter();
      });
    }
    clear.addEventListener('click', () => {
      search.value = '';
      sort.sel.value = 'recent';
      rating.sel.value = '0';
      if (product) product.sel.value = '';
      if (media) media.setAttribute('aria-pressed', 'false');
      Object.assign(state, { q: [], sort: 'recent', rating: 0, product: '', media: false });
      refilter();
      search.focus();
    });
    more.addEventListener('click', () => {
      const from = state.shown;
      state.shown += STEP;
      render(from);
    });

    render();
  };

  // Photo viewer: one <dialog> for the page, stepping through the photos
  // of the group (one review, or the summary strip) that was opened.
  let viewer = null;
  const getViewer = () => {
    if (viewer) return viewer;
    const dialog = make('dialog', { class: 'flrc-rvl-viewer', 'aria-label': 'Customer photo' });
    dialog.innerHTML =
      '<figure class="flrc-rvl-viewer__figure"><img class="flrc-rvl-viewer__img" alt="" sizes="(min-width: 64rem) 64rem, 100vw">' +
      '<figcaption class="flrc-rvl-viewer__bar"><span data-count></span><span class="flrc-rvl-viewer__nav">' +
      '<button type="button" data-prev aria-label="Previous photo">&larr;</button>' +
      '<button type="button" data-next aria-label="Next photo">&rarr;</button>' +
      '<button type="button" data-close aria-label="Close">&times;</button></span></figcaption></figure>';
    document.body.append(dialog);
    const img = dialog.querySelector('img');
    const count = dialog.querySelector('[data-count]');
    const prev = dialog.querySelector('[data-prev]');
    const next = dialog.querySelector('[data-next]');
    let group = [];
    let at = 0;
    let opener = null;

    const show = (i) => {
      at = (i + group.length) % group.length;
      const link = group[at];
      const thumb = link.querySelector('img');
      img.removeAttribute('srcset');
      img.src = link.href;
      if (link.dataset.srcset) img.srcset = link.dataset.srcset;
      img.alt = thumb ? thumb.alt : '';
      count.textContent = group.length > 1 ? `Photo ${at + 1} of ${group.length}` : 'Customer photo';
    };

    prev.addEventListener('click', () => show(at - 1));
    next.addEventListener('click', () => show(at + 1));
    dialog.querySelector('[data-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (e) => {
      if (group.length < 2) return;
      if (e.key === 'ArrowLeft') show(at - 1);
      if (e.key === 'ArrowRight') show(at + 1);
    });
    dialog.addEventListener('close', () => {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      if (opener) opener.focus();
    });

    viewer = (link) => {
      const box = link.closest('[data-flrc-rvl-photos]');
      group = box ? [...box.querySelectorAll('[data-flrc-rvl-photo]')] : [link];
      opener = link;
      prev.hidden = next.hidden = group.length < 2;
      show(group.indexOf(link));
      dialog.showModal();
    };
    return viewer;
  };

  const initPhotos = (root) => {
    if (typeof HTMLDialogElement !== 'function') return;
    root.addEventListener('click', (e) => {
      const link = e.target.closest('[data-flrc-rvl-photo]');
      if (!link || !root.contains(link)) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      getViewer()(link);
    });
    root.querySelectorAll('[data-flrc-rvl-photo]').forEach((link) => link.setAttribute('aria-haspopup', 'dialog'));
  };

  const init = () => {
    document.querySelectorAll('[data-flrc-rvl]').forEach((root) => {
      if (root.dataset.flrcRvlInit) return;
      root.dataset.flrcRvlInit = '1';
      initList(root);
      initPhotos(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', init);
})();
